import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import FontKeys from '../FontKeys';
import gameData, { GameVersions } from '../gameData';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import BigCar from './BigCar';
import CardSelectGroup from './CardSelectGroup';
import PatternCardItem from './PatternCardItem';
import PatternGroup from './PatternGroup';
import PatternMaker from './PatternMaker';
import ShapePatternMaker from './ShapePatternMaker';
import ABCyaLib from '@abcya/abcya_lib';

export default class NumberPatternGame extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    this.cardSelectGroup = null;
    this.curPattern = null;
    this.patternGroup = null;

    this.playerSpeed = null;
    this.aiSpeed = null;
    this.roundReady = null;
    this.roundComplete = null;
    this.driveOff = null;
    this.winner = null;
    this.loser = null;
    this.winBridge = null;
    this.finishLine = null;

    this.engineSound = null;
    this.curSequence = null;

    this.config = ABCyaLib.Environment.instance.gameConfig;

    this.patternMaker = gameData.gameVersion === GameVersions.Number ? new PatternMaker() : new ShapePatternMaker();

    this.playerPiece = new BigCar(this.scene, 'small');
    this.add(this.playerPiece);

    if (gameData.gameMode === 'race') {
      this.aiPiece = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._airplane_small, this).setOrigin(0);
    }

    this.pressPts = [];
    this.curRoundIndex = 1;
    this.roundReady = false;
    this.playerStopped = false;
  }

  startGame () {
    if (this.curRoundIndex === 1 && gameData.gameMode === 'race')ABCyaPhaser.Audio.playOneShot('pattern_how_to', this.scene, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    this.startRound();
  }

  startRound () {
    if (gameData.gameVersion === GameVersions.Number) {
      this.curPattern = this.patternMaker.getPattern(this.curRoundIndex);
    }
    else {
      const patternData = this.patternMaker.getPattern(this.curRoundIndex);
      this.curPattern = patternData.pattern;
      this.curSequence = patternData.sequence;
    }
    this.patternGroup = new PatternGroup(this.scene, this.curPattern);
    this.patternGroup.x = (this.scene.scale.width / 2) - (this.patternGroup.patternWidth / 2);
    this.patternGroup.y = this.scene.scale.height / 2 - 50;
    this.patternGroup.once('PATTERN_SHOWN', this.handlePatternShown, this);
    this.patternGroup.once('PATTERN_COMPLETE', this.handlePatternComplete, this);
    this.add(this.patternGroup);

    ABCyaPhaser.Tweens.addTween(this.playerPiece, 300, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);

    if (this.aiPiece) {
      this.aiPiece.x = this.patternGroup.x - this.aiPiece.width;
      ABCyaPhaser.Tweens.addTween(this.aiPiece, 300, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
    }

    this.aiSpeed = 0.75;
    this.playerSpeed = gameData.gameMode === 'race' ? 2.75 : 5;

    this.patternGroup.showPattern();
  }

  handlePatternShown () {
    this.cardSelectGroup = new CardSelectGroup(this.scene, this.curPattern);
    this.cardSelectGroup.x = (this.scene.scale.width / 2) - (this.cardSelectGroup.getBounds().width / 2);
    this.cardSelectGroup.y = this.scene.scale.height / 2 + 100;
    this.add(this.cardSelectGroup);
    this.cardSelectGroup.on('PLACE_ITEM', this.handlePatternItemPlaced, this);

    this.playerPiece.scale = 1;
    this.playerPiece.x = this.patternGroup.x - this.playerPiece.getBounds().width + (this.playerPiece.rightWheel.width / 2);
    this.playerPiece.y = this.patternGroup.y - this.playerPiece.carHeight;

    if (this.aiPiece !== null && this.aiPiece !== undefined) {
      this.aiPiece.x = this.playerPiece.x;
      this.aiPiece.y = this.playerPiece.y - this.aiPiece.height - 80;
    }

    this.emit('SHOW_ROUND', { _roundNum: this.curRoundIndex });

    this.roundReady = true;
    this.roundComplete = false;

    if (this.curRoundIndex === 1) {
      this.patternGroup.showHint();
    }

    this.playEngineSound(SoundAssetKeys._engine_start);
  }

  handlePatternItemPlaced (e) {
    /** @type {PatternCardItem} */
    const pItem = e._placedItem;
    let placed = false;

    // if within height
    const patternPosIndex = Math.round((pItem.x - (this.patternGroup.patternItemWidth / 2) + this.cardSelectGroup.x) / this.patternGroup.patternItemWidth);

    if (pItem.y + this.cardSelectGroup.y >= this.patternGroup.y - pItem.getBounds().height && pItem.y + this.cardSelectGroup.y <= this.patternGroup.y + pItem.getBounds().height) {
      if (patternPosIndex >= 0 && patternPosIndex < this.curPattern.length) {
        if (this.patternGroup.checkIndexVal(patternPosIndex) === pItem.val && this.patternGroup.checkIndexFilled(patternPosIndex) === false) placed = true;
      }
    }

    if (placed === true) {
      this.patternGroup.addToPatternAt(patternPosIndex,pItem);
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._card_placed, this.scene);
      this.playEngineSound(SoundAssetKeys._car_rev);
    }
    else {
      pItem.removeIt();
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._car_horn, this.scene);
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._card_fall, this.scene);
    }
  }

  playEngineSound (soundName) {
    if (this.engineSound !== null) {
      this.engineSound.destroy();
      this.engineSound = null;
    }
    this.engineSound = ABCyaPhaser.Audio.createSound(this.scene);
    ABCyaPhaser.Audio.playSound(soundName, this.engineSound);
  }

  endGame () {
    if (this.engineSound !== null) {
      this.engineSound.destroy();
      this.engineSound = null;
    }

    this.emit('END_GAME');
  }

  showWinner (winnerName) {
    // add big card bridge;
    const bridgeHeight = this.scene.scale.height - 80;
    this.winBridge = this.createWinBridge(bridgeHeight);

    // add flag
    this.finishLine = this.scene.add.container();
    const finishFlag = ABCyaPhaser.Images.addImage(0,0,UIAssetKeys._finish_line, this.finishLine).setOrigin(0);
    finishFlag.x = -finishFlag.width / 2;

    let winStr;

    // no winner text for practice
    if (gameData.gameMode === 'race') {
      winStr = winnerName === 'ai' ? ABCyaPhaser.Localization.instance.getLocalizedString('results.try_again') : ABCyaPhaser.Localization.instance.getLocalizedString('results.winner');
    }
    else {
      winStr = ABCyaPhaser.Localization.instance.getLocalizedString('results.great_job');
    }

    const winTxt = ABCyaPhaser.Text.addText(0, finishFlag.height + 10, '40pt', 0xffffff, FontKeys.carton_sixregular, winStr, this.finishLine, true).setOrigin(0.5);

    this.finishLine.scale = 0;
    this.finishLine.alpha = 0;
    this.finishLine.x = this.scene.scale.width / 2;
    this.finishLine.y = 10;
    this.add(this.finishLine);
    ABCyaPhaser.Tweens.addTween(this.finishLine, 500, { alpha: 1, scale: 1, easeParams: [1, 0.3] }, Phaser.Math.Easing.Elastic.Out);

    if (this.winner) {
      this.winner = null;
    }

    let width = 0;
    if (winnerName === 'ai') {
      this.winner = ABCyaPhaser.Images.addImage(0,this.scene.scale.height / 2, UIAssetKeys._airplane_large, this).setOrigin(0);
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._game_over_funny, this.scene);
      width = this.winner.width;
    }
    else {
      this.winner = new BigCar(this.scene, 'large');
      this.add(this.winner);
      this.winner.y = bridgeHeight - this.winner.carHeight;
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cartoon_win_1, this.scene);
      width = this.winner.getBounds().width;
    }
    this.winner.winnerName = winnerName;
    this.winner.scaleX = -1;
    this.winner.x = this.scene.scale.width + width;
    this.winner.driveBy = true;

    // fade out pattern and select
    ABCyaPhaser.Tweens.addTween(this.patternGroup, 300, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out, (tween, targets) => targets.forEach(t => t.destroy()));
    this.patternGroup = null;
    ABCyaPhaser.Tweens.addTween(this.cardSelectGroup, 300, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out, (tween, targets) => targets.forEach(t => t.destroy()));
    this.cardSelectGroup = null;

    if (this.loser) {
      ABCyaPhaser.Tweens.addTween(this.loser, 300, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
    }

    this.playEngineSound(SoundAssetKeys._pass);
  }

  createWinBridge (bridgeY) {
    const cardScale = 2.5;
    const cardSample = this.scene.textures.getFrame(ABCyaPhaser.Images.DEFAULT_ATLAS, UIAssetKeys._card_bg);
    const cardSize = cardSample.realWidth * cardScale;
    const cardSpacing = 20;
    const totalWidth = (5 * (cardSize + cardSpacing)) - cardSpacing;
    const startX = (this.scene.scale.width / 2) - (totalWidth / 2);
    const bridge = [];
    while (bridge.length < 5) {
      const bridgePiece = ABCyaPhaser.Images.addImage(startX + (bridge.length * (cardSize + cardSpacing)),bridgeY, UIAssetKeys._card_bg, this).setOrigin(0);
      bridgePiece.scale = cardScale;
      bridge.push(bridgePiece);
    }
    return bridge;
  }

  handlePatternComplete () {
    if (this.playerStopped === true) this.playEngineSound(SoundAssetKeys._car_rev);
    this.playerStopped = false;
    this.endRound(true);
  }

  endRound (isWon) {
    this.emit('END_ROUND');
    this.roundReady = false;
    this.cardSelectGroup.off('PLACE_ITEM', this.handlePatternItemPlaced, this);
    this.cardSelectGroup.cleanUpSelect();

    this.winner = isWon === false && gameData.gameMode === 'race' ? this.aiPiece : this.playerPiece;
    this.playerSpeed = isWon === true ? 7 : 0.5;
    this.playerPiece.driveOff = true;
    if (gameData.gameMode === 'race') {
      this.loser = isWon === true ? this.aiPiece : this.playerPiece;
      this.aiSpeed = isWon === true ? 0.5 : 7;
      this.aiPiece.driveOff = true;
    }
  }

  cleanUpRound (roundComplete) {
    this.winner = null;
    this.loser = null;

    // get rid of bridge and winner text, finish line
    this.finishLine.destroy();
    while (this.winBridge.length > 0) {
      this.winBridge.shift().destroy();
    }

    // reset car and plane
    this.playerPiece.driveOff = false;
    if (this.aiPiece) this.aiPiece.driveOff = false;

    // clear out pattern and select
    this.patternGroup = null;
    this.cardSelectGroup = null;

    // only if round won by player
    if (roundComplete === true) {
      if (this.curRoundIndex < 10) {
        this.curRoundIndex++;
        this.startRound();
      }
      else {
        this.endGame();
      }
    }
    else {
      // replace pattern
      if (gameData.gameVersion === GameVersions.Number) this.patternMaker.replacePattern(this.curPattern[0].val);
      else this.patternMaker.replacePattern(this.curSequence);

      // restart same round
      this.startRound();
    }
  }

  updateGame () {
    if (this.roundReady === true && this.roundComplete === false) {

      // get index of car position
      const patternPosIndex = Math.floor((this.playerPiece.x + this.playerPiece.getBounds().width - this.patternGroup.x) / this.patternGroup.patternItemWidth);

      if (patternPosIndex < this.patternGroup.pattern.length) {
        // stop at spot?
        const filled = this.patternGroup.checkIndexFilled(patternPosIndex);
        if (filled === true) {
          this.playerPiece.x += this.playerSpeed;
          this.playerPiece.turnWheels(this.playerSpeed);

          this.playerStopped = false;
        }
        else {
          // stop car
          // play skid sound just once?
          if (this.playerStopped === false) this.playEngineSound(SoundAssetKeys._tire_skid_squeal08);
          this.playerStopped = true;
        }
      }
      else {
        // win race
        this.endRound(true);
      }

      // move airplane
      if (gameData.gameMode === 'race') {
        this.aiPiece.x += this.aiSpeed;
        const aiPos = Math.floor((this.aiPiece.x + this.aiPiece.width - this.patternGroup.x) / this.patternGroup.patternItemWidth);
        if (aiPos > this.patternGroup.pattern.length - 1) {
          // ai wins
          this.endRound(false);
        }
      }
    }
    else {
      if (this.playerPiece.driveOff === true) {

        if (this.playerPiece.x + this.playerSpeed > this.scene.scale.width + 20) {
          this.playerPiece.driveOff = false;
          if (this.playerPiece === this.winner) this.showWinner('player');
        }
        else {
          this.playerPiece.x += this.playerSpeed;
          this.playerPiece.turnWheels(this.playerSpeed);
        }
      }

      if (this.aiPiece && this.aiPiece.driveOff === true) {
        if (this.aiPiece.x + this.aiSpeed > this.scene.scale.width + 20) {
          this.aiPiece.driveOff = false;
          if (this.aiPiece === this.winner) this.showWinner('ai');
        }
        else {
          this.aiPiece.x += this.aiSpeed;
        }
      }

      if (this.winner && this.winner.driveBy === true) {// move big car here
        let width = 0;
        if (this.winner instanceof Phaser.GameObjects.Image) width = this.winner.width;
        else if (this.winner instanceof BigCar) width = this.winner.getBounds().width;
        if (this.winner.x - 15 >= -width) {
          this.winner.x -= 15;
          if (this.winner.winnerName === 'player') {
            this.winner.turnWheels(-10);
          }
        }
        else {
          const roundWon = this.winner.winnerName === 'player';
          this.cleanUpRound(roundWon);
        }
      }
    }
  }
}